import { useEffect, useRef, useState } from 'react';

interface MultiSelectDropdownProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (value: string[]) => void;
}

export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  function toggle(option: string) {
    if (selected.includes(option)) {
      onChange(selected.filter((s) => s !== option));
    } else {
      onChange([...selected, option]);
    }
  }

  const allSelected = options.length > 0 && selected.length === options.length;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent ${
          selected.length > 0
            ? 'border-accent/50 bg-accent/10 text-accent'
            : 'border-border bg-bg-surface text-text-primary hover:bg-bg-elevated'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {label}
        {selected.length > 0 && (
          <span className="flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-white">
            {selected.length}
          </span>
        )}
        <svg
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          aria-label={`Filter by ${label}`}
          className="absolute left-0 top-full z-30 mt-1 min-w-[160px] rounded-lg border border-border bg-bg-primary shadow-lg"
        >
          {/* Select all / Clear row */}
          <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
            <button
              type="button"
              onClick={() => onChange(allSelected ? [] : [...options])}
              className="text-xs text-accent hover:underline focus:outline-none"
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
            <span className="text-xs text-text-secondary">
              {selected.length}/{options.length}
            </span>
          </div>

          {/* Options */}
          <ul className="max-h-52 overflow-y-auto py-1">
            {options.map((option) => {
              const checked = selected.includes(option);
              return (
                <li key={option}>
                  <label className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 text-sm text-text-primary hover:bg-bg-elevated">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(option)}
                      className="h-3.5 w-3.5 rounded border-border accent-accent"
                    />
                    {option}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
